import { useState, useEffect } from 'react';
import { getSupabaseClient } from '@/lib/supabase';
import { AdminPageLayout } from '@/components/layout/AdminPageLayout';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

interface ErrorIssue {
  id: string;
  title: string;
  error_type: string;
  status: string;
  priority: string;
  total_occurrences: number;
  affected_users: number;
  first_seen: string;
  last_seen: string;
  fingerprint: string;
}

export default function ErrorsPage() {
  const [issues, setIssues] = useState<ErrorIssue[]>([]);
  const [filter, setFilter] = useState<'all' | 'unresolved' | 'resolved'>('unresolved');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIssues();
  }, [filter]);

  const loadIssues = async () => {
    setLoading(true);
    try {
      const client = getSupabaseClient();
      let query = client
        .from('error_issues')
        .select('*')
        .order('last_seen', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query.limit(100);
      if (error) throw error;
      if (data) setIssues(data);
    } catch (error) {
      console.error('Error loading issues:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminPageLayout
      title="🐛 Error Tracking"
      subtitle="Monitor and resolve application errors"
      actions={
        <div className="flex gap-2">
          <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded font-semibold ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>All</button>
          <button onClick={() => setFilter('unresolved')} className={`px-3 py-1 rounded font-semibold ${filter === 'unresolved' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>Unresolved</button>
          <button onClick={() => setFilter('resolved')} className={`px-3 py-1 rounded font-semibold ${filter === 'resolved' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>Resolved</button>
        </div>
      }
    >
      <div className="bg-slate-900 rounded-lg border border-slate-800 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-800">
            <tr>
              <th className="p-4 text-left text-slate-300 font-semibold">Error</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Type</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Priority</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Occurrences</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Users</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Last Seen</th>
              <th className="p-4 text-left text-slate-300 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Loading...</td></tr>
            ) : issues.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">No issues found</td></tr>
            ) : (
              issues.map((issue) => (
                <tr key={issue.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                  <td className="p-4 text-slate-300 font-medium">{issue.title}</td>
                  <td className="p-4 text-slate-400 text-sm">{issue.error_type}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${issue.priority === 'critical' ? 'bg-red-900 text-red-400' : issue.priority === 'high' ? 'bg-orange-900 text-orange-400' : 'bg-slate-700 text-slate-300'}`}>
                      {issue.priority}
                    </span>
                  </td>
                  <td className="p-4 text-slate-300">{issue.total_occurrences}</td>
                  <td className="p-4 text-slate-300">{issue.affected_users}</td>
                  <td className="p-4 text-slate-400 text-sm">{new Date(issue.last_seen).toLocaleDateString()}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${issue.status === 'resolved' ? 'bg-green-900 text-green-400' : 'bg-yellow-900 text-yellow-400'}`}>
                      {issue.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </AdminPageLayout>
  );
}
